/**
 * The Calendar view: a month grid, an agenda for the next thirty days, one form
 * that both adds and edits, and the subscribe link Google, Apple and Outlook
 * read. Anything the companion booked out loud carries a pill, so the player
 * can see what was made by voice and correct it.
 */

const KINDS = [
  { id: 'practice', label: 'Practice', pill: 'hot' },
  { id: 'game', label: 'Game', pill: 'grape' },
  { id: 'workout', label: 'Workout', pill: 'good' },
  { id: 'appointment', label: 'Appointment', pill: 'cool' },
  { id: 'other', label: 'Other', pill: 'mute' },
];
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const $ = (id) => document.getElementById(id);
const kindOf = (id) => KINDS.find((k) => k.id === id) || KINDS[KINDS.length - 1];

const startOfMonth = (d) => { const x = new Date(d); x.setDate(1); x.setHours(0, 0, 0, 0); return x; };
const addMonths = (d, n) => { const x = startOfMonth(d); x.setMonth(x.getMonth() + n); return x; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const daysInMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
const pad = (n) => String(n).padStart(2, '0');
const dayKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const noon = (key) => new Date(`${key}T12:00`);
const timeOf = (d) => d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
const shortDay = (d) => d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
const longDay = (d) => d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });

let ctx = null;
let cursor = startOfMonth(new Date());   // the month on screen
let events = [];
let editingId = null;
let selected = null;                     // the day the player tapped, YYYY-MM-DD
let feedUrl = null;

/* ------------------------------------------------------------------ mount */

export function mount(c) {
  ctx = c;
  const root = $('calendarRoot');
  if (!root) return;
  root.innerHTML = shell();
  bind();
}

function shell() {
  return `
  <div class="cal-wrap">
    <div class="card cal-month">
      <h3>Month</h3>
      <div class="cal-head">
        <button class="btn" id="calPrev" title="Previous month">&#x2039;</button>
        <b id="calLabel"></b>
        <button class="btn" id="calNext" title="Next month">&#x203A;</button>
        <button class="btn" id="calToday">Today</button>
      </div>
      <div class="cal-dow">${DOW.map((d) => `<span>${d}</span>`).join('')}</div>
      <div class="cal-grid" id="calGrid"></div>
      <div class="cal-dayof" id="calDay"></div>
    </div>

    <div class="card cal-form">
      <h3 id="calFormTitle">Add an event</h3>
      <label>Title<input id="calTitle" type="text" maxlength="120" placeholder="Team practice"></label>
      <label>Kind<select id="calKind">${KINDS.map((k) => `<option value="${k.id}">${k.label}</option>`).join('')}</select></label>
      <div class="cal-two">
        <label>Date<input id="calDate" type="date"></label>
        <label>Time<input id="calTime" type="time"></label>
      </div>
      <div class="cal-two">
        <label>Minutes<input id="calDur" type="number" min="5" max="600" step="5"></label>
        <label>Place<input id="calPlace" type="text" maxlength="120" placeholder="School gym"></label>
      </div>
      <label>Notes<textarea id="calNotes" rows="2" maxlength="500" placeholder="Bring the good ball"></textarea></label>
      <div class="cal-actions">
        <button class="btn primary wide" id="calSave">Add to calendar</button>
        <button class="btn" id="calCancel" hidden>Cancel</button>
      </div>
      <p class="sub cal-hint">You can also just say it. Tell your companion "book practice tomorrow at five" and it lands here.</p>
    </div>

    <div class="card cal-agenda">
      <h3>Next thirty days</h3>
      <div id="calList"></div>
    </div>

    <div class="card cal-feed">
      <h3>Subscribe</h3>
      <p class="sub">Put this link in a calendar app and every practice, game and appointment shows up there too. It keeps itself up to date.</p>
      <div class="cal-feed-row">
        <input id="calFeedUrl" type="text" readonly hidden>
        <button class="btn" id="calCopy" hidden>Copy link</button>
      </div>
      <p class="sub" id="calFeedBox">Getting your link...</p>
      <div class="cal-help">
        <div><b>Google Calendar</b> Other calendars, the plus sign, From URL, paste the link.</div>
        <div><b>Apple Calendar</b> File, New Calendar Subscription, paste the link.</div>
        <div><b>Outlook</b> Add calendar, Subscribe from web, paste the link.</div>
      </div>
    </div>
  </div>`;
}

function bind() {
  const root = $('calendarRoot');
  $('calPrev').onclick = () => { cursor = addMonths(cursor, -1); render(); };
  $('calNext').onclick = () => { cursor = addMonths(cursor, 1); render(); };
  $('calToday').onclick = () => { cursor = startOfMonth(new Date()); selected = dayKey(new Date()); render(); };
  $('calSave').onclick = save;
  $('calCancel').onclick = resetForm;
  $('calCopy').onclick = copyFeed;
  // One listener for everything the grid and the agenda repaint.
  root.addEventListener('click', (e) => {
    const edit = e.target.closest('[data-edit]');
    if (edit) return startEdit(edit.dataset.edit);
    const del = e.target.closest('[data-del]');
    if (del) return remove(del.dataset.del);
    const cell = e.target.closest('.cal-cell');
    if (!cell) return;
    selected = cell.dataset.date;
    if (!editingId) $('calDate').value = selected;
    paintMonth(); paintDay();
  });
  resetForm();
}

/* ----------------------------------------------------------------- render */

export async function render() {
  if (!ctx || !$('calGrid')) return;
  $('calLabel').textContent = cursor.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  try {
    // One fetch covers the month on screen and the thirty day agenda.
    const tz = new Date().getTimezoneOffset();
    const from = dayKey(new Date(Math.min(addDays(cursor, -7).getTime(), Date.now() - 7 * 864e5)));
    const to = dayKey(new Date(Math.max(addDays(addMonths(cursor, 1), 7).getTime(), Date.now() + 60 * 864e5)));
    const r = await ctx.api(`/api/events?from=${from}&to=${to}&tz=${tz}`);
    events = Array.isArray(r.events) ? r.events : [];
    paintMonth(); paintAgenda(); paintDay();
  } catch (e) {
    events = [];
    paintMonth(); paintDay();
    $('calList').innerHTML = `<p class="sub">Could not load your calendar: ${esc(e.message)}</p>`;
  }
  paintFeed();
}

/** Events grouped by the day they fall on, chronological, keys in order. */
function byDay(list) {
  const out = new Map();
  [...list]
    .filter((e) => e && Number.isFinite(Date.parse(e.at)))
    .sort((a, b) => Date.parse(a.at) - Date.parse(b.at))
    .forEach((e) => {
      const k = dayKey(new Date(e.at));
      if (!out.has(k)) out.set(k, []);
      out.get(k).push(e);
    });
  return out;
}

function paintMonth() {
  const first = startOfMonth(cursor);
  const start = addDays(first, -first.getDay());
  const weeks = Math.ceil((first.getDay() + daysInMonth(first)) / 7);
  const today = dayKey(new Date());
  const groups = byDay(events);
  const cells = [];
  for (let i = 0; i < weeks * 7; i++) {
    const d = addDays(start, i);
    const k = dayKey(d);
    const list = groups.get(k) || [];
    const cls = ['cal-cell'];
    if (d.getMonth() !== first.getMonth()) cls.push('out');
    if (k === today) cls.push('today');
    if (k === selected) cls.push('sel');
    cells.push(`<div class="${cls.join(' ')}" data-date="${k}">
      <span class="d">${d.getDate()}</span>
      ${list.slice(0, 3).map((e) => `<button class="cal-ev k-${esc(kindOf(e.kind).id)}" data-edit="${esc(e.id)}" title="${esc(e.title)}"><span class="t">${esc(timeOf(new Date(e.at)))}</span> ${esc(e.title)}</button>`).join('')}
      ${list.length > 3 ? `<span class="more">plus ${list.length - 3} more</span>` : ''}
    </div>`);
  }
  $('calGrid').innerHTML = cells.join('');
}

function paintDay() {
  const box = $('calDay');
  if (!box) return;
  if (!selected) { box.innerHTML = ''; return; }
  const list = byDay(events).get(selected) || [];
  box.innerHTML = `<div class="cal-dayhead"><b>${esc(longDay(noon(selected)))}</b>
      <span class="sub">${list.length ? `${list.length} on this day` : 'nothing booked yet'}</span></div>
    ${list.map(row).join('')}`;
}

function paintAgenda() {
  const now = Date.now();
  const soon = events.filter((e) => {
    const t = Date.parse(e.at);
    return Number.isFinite(t) && t >= now - 36e5 && t <= now + 30 * 864e5;
  });
  if (!soon.length) {
    // Not "on the right": under 900px the form sits above this card, not beside it.
    $('calList').innerHTML = '<p class="sub">Nothing on the calendar for the next thirty days. Add one with the form, or tell your companion out loud.</p>';
    return;
  }
  $('calList').innerHTML = [...byDay(soon).entries()].map(([k, list]) => `
    <div class="cal-day"><b>${esc(shortDay(noon(k)))}</b></div>
    ${list.map(row).join('')}`).join('');
}

function row(e) {
  const k = kindOf(e.kind);
  const d = new Date(e.at);
  return `<div class="cal-row">
    <div class="cal-when"><b>${esc(timeOf(d))}</b><small>${Number(e.durationMin) || 60} min</small></div>
    <div class="cal-what">
      <b>${esc(e.title)}</b>
      <div class="cal-tags">
        <span class="pill ${k.pill}">${k.label}</span>
        ${e.place ? `<span class="cal-place">${esc(e.place)}</span>` : ''}
        ${e.createdBy === 'companion' ? '<span class="pill voice">Booked by voice</span>' : ''}
      </div>
      ${e.notes ? `<p class="cal-note">${esc(e.notes)}</p>` : ''}
    </div>
    <div class="cal-ops">
      <button class="btn" data-edit="${esc(e.id)}">Edit</button>
      <button class="btn" data-del="${esc(e.id)}">Delete</button>
    </div>
  </div>`;
}

/* ------------------------------------------------------------------- form */

function resetForm() {
  editingId = null;
  ['calTitle', 'calPlace', 'calNotes'].forEach((id) => { $(id).value = ''; });
  $('calKind').value = 'practice';
  $('calDate').value = selected || dayKey(new Date());
  $('calTime').value = '17:00';
  $('calDur').value = 60;
  $('calFormTitle').textContent = 'Add an event';
  $('calSave').textContent = 'Add to calendar';
  $('calCancel').hidden = true;
}

function startEdit(id) {
  const e = events.find((x) => x.id === id);
  if (!e) return;
  const d = new Date(e.at);
  editingId = id;
  $('calTitle').value = e.title || '';
  $('calKind').value = kindOf(e.kind).id;
  $('calDate').value = dayKey(d);
  $('calTime').value = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  $('calDur').value = Number(e.durationMin) || 60;
  $('calPlace').value = e.place || '';
  $('calNotes').value = e.notes || '';
  $('calFormTitle').textContent = 'Edit event';
  $('calSave').textContent = 'Save changes';
  $('calCancel').hidden = false;
  $('calTitle').focus();
}

async function save() {
  const title = $('calTitle').value.trim();
  if (title.length < 2) return ctx.toast('Give the event a name first.', true);
  const date = $('calDate').value;
  const time = $('calTime').value;
  if (!date || !time) return ctx.toast('Pick a date and a time.', true);
  const at = new Date(`${date}T${time}`);
  if (Number.isNaN(at.getTime())) return ctx.toast('That date and time did not read right.', true);
  const payload = {
    title, kind: $('calKind').value, at: at.toISOString(),
    durationMin: Number($('calDur').value) || 60,
    place: $('calPlace').value.trim(), notes: $('calNotes').value.trim(),
  };
  const editing = editingId;
  $('calSave').disabled = true;
  try {
    if (editing) await ctx.api(`/api/events/${editing}`, { method: 'PATCH', body: JSON.stringify(payload) });
    else await ctx.api('/api/events', { method: 'POST', body: JSON.stringify(payload) });
    ctx.toast(editing ? 'Event updated.' : `Added to your calendar: ${title}`);
    resetForm();
    await render();
  } catch (e) {
    ctx.toast(e.message, true);
  } finally { $('calSave').disabled = false; }
}

async function remove(id) {
  const e = events.find((x) => x.id === id);
  const ok = await ctx.ui.confirm({
    title: 'Delete this event?',
    body: `${e ? e.title : 'This event'} comes off your calendar and out of the feed anyone subscribed to.`,
    confirmText: 'Delete', danger: true,
  });
  if (!ok) return;
  try {
    await ctx.api(`/api/events/${id}`, { method: 'DELETE' });
    if (editingId === id) resetForm();
    ctx.toast('Event deleted.');
    await render();
  } catch (err) { ctx.toast(err.message, true); }
}

/* ------------------------------------------------------------------- feed */

async function paintFeed() {
  const box = $('calFeedBox');
  if (!box || feedUrl) return;
  try {
    const r = await ctx.api('/api/events/feed');
    feedUrl = r.url;
    $('calFeedUrl').value = feedUrl;
    $('calFeedUrl').hidden = false;
    $('calCopy').hidden = false;
    box.textContent = 'Anyone with this link can read your calendar, so only share it with people you trust.';
  } catch (e) {
    box.textContent = `No subscribe link yet: ${e.message}`;
  }
}

async function copyFeed() {
  if (!feedUrl) return;
  try {
    await navigator.clipboard.writeText(feedUrl);
    ctx.toast('Calendar link copied.');
  } catch {
    $('calFeedUrl').select();
    ctx.toast('The link is selected. Copy it with your keyboard.');
  }
}
